import { useRef, useState } from 'react';
import { DEFAULT_CODE_LENGTH } from '@/src/components/CodeInput';

export type UseOtpCodeEntryOptions = {
  /** Fires once when the code reaches full length, unless those exact digits were already attempted. */
  onComplete: (code: string) => void;
  length?: number;
};

/**
 * State for a one-time code being typed into a CodeInput: the digits, the lock after the
 * server retires a code, and the guard that keeps a rejected code from being re-submitted
 * on every keystroke. Verifying is the caller's — this only decides when an attempt may start.
 */
export function useOtpCodeEntry({ onComplete, length = DEFAULT_CODE_LENGTH }: UseOtpCodeEntryOptions) {
  const [code, setCode] = useState('');
  const [entryLocked, setEntryLocked] = useState(false);
  // Guards the auto-submit so a rejected code is not retried on every keystroke.
  const attemptedCodeRef = useRef('');

  const handleCodeChange = (next: string) => {
    setCode(next);
    // Autofill and paste deliver all digits at once; submitting on completion saves the
    // extra tap without stealing the choice from someone typing.
    if (next.length === length && next !== attemptedCodeRef.current) {
      onComplete(next);
    }
  };

  /** Claims the digits for a verify attempt; false when entry is locked or the code is short. */
  const beginAttempt = (codeToVerify: string) => {
    if (entryLocked || codeToVerify.length !== length) {
      return false;
    }
    attemptedCodeRef.current = codeToVerify;
    return true;
  };

  return {
    code,
    isComplete: code.length === length,
    entryLocked,
    handleCodeChange,
    beginAttempt,
    // Retrying a code should not start with six backspaces. The attempt is deliberately
    // kept, so retyping the same digits does not auto-submit and burn another attempt.
    rejectCode: () => setCode(''),
    lockEntry: () => setEntryLocked(true),
    resetForNewCode: () => {
      setCode('');
      setEntryLocked(false);
      attemptedCodeRef.current = '';
    },
  };
}
